import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import type { Section } from '../../data/referenceGuides/types';
interface TOCProps {
  sections: Section[];
  activeId: string;
}
function scrollToSection(pathname: string, id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  // Update the URL so the section is shareable, without adding a history entry
  // per click and without dropping the HashRouter route. HashRouter encodes
  // the route and the section anchor in one browser hash: #/route#section.
  const url = new URL(window.location.href);
  url.hash = `#${pathname}#${id}`;
  history.replaceState(null, '', url);
  el.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
  el.focus({
    preventScroll: true
  });
}

/** Sticky rail, lg and up. */
export function ReferenceGuideTOCRail({
  sections,
  activeId
}: TOCProps) {
  const {
    pathname
  } = useLocation();
  return <nav aria-label="On this page" className="sticky top-28" data-id="element-356" data-mp="x3sjqr7ain4s">
      <p className="text-white/50 text-xs font-bold uppercase tracking-widest mb-4" data-id="element-357" data-mp="s4fxa7kbzi2q">On this page</p>
      <ul className="space-y-1 border-l border-white/[0.1] list-none" data-id="element-358" data-mp="ndpsavfthh8k">
        {sections.map(s => {
        const active = s.id === activeId;
        return <li key={s.id} data-id="element-359" data-mp="idtn4svdvjrf">
              <a href={`#${s.id}`} aria-current={active ? 'location' : undefined} onClick={e => {
            e.preventDefault();
            scrollToSection(pathname, s.id);
          }} className={`flex items-center min-h-[44px] pl-4 -ml-px border-l-2 text-sm transition-colors ${active ? 'border-violet-link text-white font-medium' : 'border-transparent text-white/55 hover:text-white'}`} data-id="element-360" data-mp="icilayli0m2y">
                {s.tocLabel}
              </a>
            </li>;
      })}
      </ul>
    </nav>;
}

/**
 * Mobile section jumper.
 *
 * QA-003: the desktop rail hides below `lg` and nothing replaced it, leaving a
 * seven-section document with no navigation on the most common viewport. This
 * is that replacement — a sticky trigger under the navbar opening a dialog.
 *
 * Focus is managed properly: it moves into the dialog on open, Escape closes,
 * and focus returns to the trigger on close. A dropdown that strands keyboard
 * focus is worse than no dropdown.
 */
export function ReferenceGuideTOCMobile({
  sections,
  activeId
}: TOCProps) {
  const {
    pathname
  } = useLocation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const activeIndex = Math.max(0, sections.findIndex(s => s.id === activeId));
  const activeSection = sections[activeIndex];
  useEffect(() => {
    if (!open) return;
    const firstLink = panelRef.current?.querySelector<HTMLAnchorElement>('a');
    firstLink?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  return <div className="lg:hidden sticky top-16 z-30 -mx-6 px-6 bg-navy/95 backdrop-blur border-b border-white/[0.08]" data-id="element-361" data-mp="1ho9ul3ddep7r">
      <button ref={triggerRef} type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-haspopup="dialog" className="w-full min-h-[44px] flex items-center justify-between gap-3 text-left" data-id="element-362" data-mp="1do7omi0lr9mo">
        <span className="text-white/80 text-sm font-medium truncate" data-id="element-363" data-mp="l0vex8fjiwas">
          <span className="text-white/40" data-id="element-364" data-mp="yrgzd1kn7eas">{activeIndex + 1}.</span> {activeSection?.tocLabel}
        </span>
        <span className="flex items-center gap-1.5 text-white/50 text-xs flex-shrink-0" data-id="element-365" data-mp="tyxah8gi8wqo">
          of {sections.length}
          <ChevronDownIcon aria-hidden="true" className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-366" data-mp="68bg2jzfbaz5" />
        </span>
      </button>

      {open && <div ref={panelRef} role="dialog" aria-label="On this page" aria-modal="false" className="absolute left-0 right-0 top-full bg-navy border-b border-white/[0.1] shadow-2xl px-6 py-3" data-id="element-367" data-mp="eyjoqam3r7uo">
          <div className="flex items-center justify-between mb-1" data-id="element-368" data-mp="ovg1isj1ag51">
            <p className="text-white/50 text-xs font-bold uppercase tracking-widest" data-id="element-369" data-mp="ou4xqspl98dx">On this page</p>
            <button type="button" onClick={close} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-white/50 hover:text-white" data-id="element-370" data-mp="tohkbbmd2ztw">
              <XIcon aria-hidden="true" className="w-4 h-4" data-id="element-371" data-mp="12mb9m2lpkt7k" />
              <span className="sr-only" data-id="element-372" data-mp="17mpbf2wpv6x5">Close section list</span>
            </button>
          </div>
          <ul className="list-none" data-id="element-373" data-mp="1clad76rm9ynp">
            {sections.map((s, i) => {
          const active = s.id === activeId;
          return <li key={s.id} data-id="element-374" data-mp="39eltkvde3f0w" data-mpi={i}>
                  <a href={`#${s.id}`} aria-current={active ? 'location' : undefined} onClick={e => {
              e.preventDefault();
              scrollToSection(pathname, s.id);
              close();
            }} className={`flex items-center gap-3 min-h-[44px] text-sm ${active ? 'text-white font-medium' : 'text-white/60'}`} data-id="element-375" data-mp="39fn9bd5copoy">
                    <span className="text-white/30 text-xs w-4" data-id="element-376" data-mp="2x3bfq2dyitwd">{i + 1}</span>
                    {s.tocLabel}
                  </a>
                </li>;
        })}
          </ul>
        </div>}
    </div>;
}